import React, { useState, useEffect, useCallback, useRef } from 'react';
import API from '../api';
import PoolInvite from '../components/PoolInvite';
import PoolChat from '../components/PoolChat';
import ConfirmationsPanel from '../components/ConfirmationsPanel';
import UserStatsModal from '../components/UserStatsModal';

const ps = {
  emptyText: { color:'#334', fontSize:14, padding:'16px 0', textAlign:'center' },
  publicCard: { background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:12, padding:'16px 20px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, marginBottom:10 },
  publicCardName: { fontFamily:"'Oswald', sans-serif", fontSize:16, fontWeight:700, color:'#fff', marginBottom:2 },
  publicCardMeta: { fontSize:12, color:'#556' },
};


/* ── Confetti burst ───────────────────────────────────────────────────── */
function ConfettiBurst({ active }) {
  if (!active) return null;
  const pieces = Array.from({ length: 22 }, (_, i) => i);
  const colors = ['#D50A0A','#ffd700','#7fb3ff','#4caf50','#ff9800','#fff','#013369'];
  return (
    <div style={{ position:'fixed', top:0, left:0, width:'100%', height:'100%', pointerEvents:'none', zIndex:9999 }}>
      {pieces.map(i => {
        const color = colors[i % colors.length];
        const x = 30 + Math.random() * 40;
        const angle = Math.random() * 360;
        const size = 6 + Math.random() * 8;
        const dur = 0.7 + Math.random() * 0.5;
        const dist = 80 + Math.random() * 120;
        const dx = Math.cos(angle * Math.PI / 180) * dist;
        const dy = Math.sin(angle * Math.PI / 180) * dist - 60;
        return (
          <div key={i} style={{
            position:'absolute',
            left: x + 'vw',
            top: '50vh',
            width: size,
            height: size,
            borderRadius: i % 3 === 0 ? '50%' : 2,
            background: color,
            animation: `confetti-fly ${dur}s ease-out forwards`,
            '--dx': dx + 'px',
            '--dy': dy + 'px',
          }} />
        );
      })}
      <style>{`
        @keyframes confetti-fly {
          0%   { transform: translate(0,0) rotate(0deg); opacity:1; }
          100% { transform: translate(var(--dx), var(--dy)) rotate(720deg); opacity:0; }
        }
      `}</style>
    </div>
  );
}

/* ── Rank badge ───────────────────────────────────────────────────────── */
/**
 * What a commissioner sees when their pool has nobody in it but them.
 *
 * This exists because of a measured leak, not a hunch. Of the first six people
 * the ads brought in, five joined no pool at all and the sixth created one and
 * left it at one member for two days. Recreating that path on a fresh account
 * showed why: every tool worked, and nothing asked. The card handed you a rank,
 * a pick count and a correct count — a scoreboard for a league that did not
 * exist yet — while the email invite sat behind a tab, below the rename and
 * code controls, at the bottom of a scroll. It did not look unfinished. It
 * looked done.
 *
 * So this takes the space the rank badge and stats row used to hold, says the
 * true thing plainly, and puts the two ways of inviting people right there.
 * `NoPoolYet` already does exactly this one step earlier, for somebody in no
 * pool at all; this is the same idea for the step after.
 *
 * Share comes first on purpose. It opens the phone's own share sheet with the
 * message and link already written, so the invite arrives from a number the
 * recipient knows. Email is second because it needs typing but delivers a real
 * link. The code is last: it is the fallback for reading out loud, not the
 * primary path, even though it used to be the only thing on screen.
 */
/**
 * What to say before a pool is opened to strangers.
 *
 * Sharing a pool is what grants DM rights, so publishing does not only invite
 * players, it makes everybody already in the pool reachable by whoever turns up.
 * The commissioner is making that call on other people's behalf, so the warning
 * counts them rather than leaving it to be worked out.
 */
function publishWarning(pool) {
  const others = Math.max(0, (pool.member_count || 1) - 1);
  const reach = others > 0
    ? `Anyone who joins can also message the ${others} ${others === 1 ? 'person' : 'people'} already in it.`
    : 'Anyone who joins can also message you.';
  return `List "${pool.name}" in Discover Pools?

Anyone with an account will be able to find it and join without an invite code.

${reach}

You can take it back out at any time.`;
}

function EmptyPoolPrompt({ pool, onShare, onCopy, copied, onTogglePublic, publishing }) {
  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(213,10,10,0.10), rgba(1,51,105,0.16))',
      border: '1px solid rgba(213,10,10,0.30)',
      borderRadius: 10, padding: '16px 18px', marginBottom: 14
    }}>
      <div style={{
        fontFamily: "'Oswald', sans-serif", fontSize: 16, fontWeight: 700,
        color: '#fff', letterSpacing: 0.5, marginBottom: 6
      }}>
        It's just you in here so far
      </div>
      <div style={{ color: '#aab', fontSize: 13.5, lineHeight: 1.6, marginBottom: 14 }}>
        A pool needs people. Send the invite and {pool.name} starts the moment they
        join. Every pick they make counts from that week on, so the sooner they're
        in, the more football you get together.
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          onClick={onShare}
          style={{
            padding: '11px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
            background: 'linear-gradient(135deg,#D50A0A,#a00808)', color: '#fff',
            fontFamily: "'Oswald', sans-serif", fontSize: 14, fontWeight: 700, letterSpacing: 0.8
          }}
        >📣 SHARE THE INVITE</button>
        <button
          onClick={onCopy}
          style={{
            padding: '11px 16px', borderRadius: 8, cursor: 'pointer',
            background: copied ? 'rgba(76,175,80,0.18)' : 'rgba(255,255,255,0.06)',
            border: copied ? '1px solid rgba(76,175,80,0.45)' : '1px solid rgba(255,255,255,0.15)',
            color: copied ? '#81c784' : '#aab', fontSize: 13, fontWeight: 700
          }}
        >{copied ? '✓ Code copied' : `Copy code ${pool.invite_code}`}</button>
      </div>

      {/* The strongest channel, and previously the hardest to find. */}
      <PoolInvite pool={pool} />

      {/* The other way out, and the whole point of this feature. Handing out a
          code assumes you know somebody to hand it to. Six pools sat at one
          member each because that assumption was the only option on offer. */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: 14, paddingTop: 14 }}>
        {pool.is_public ? (
          <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: '#81c784', fontWeight: 700 }}>Listed in Discover Pools</span>
            <span style={{ fontSize: 12.5, color: '#889', flex: 1, minWidth: 180 }}>
              People looking for a pool can find {pool.name} and join without a code.
            </span>
            <button
              onClick={() => onTogglePublic(pool)}
              disabled={publishing}
              style={{
                padding: '6px 12px', borderRadius: 6, background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.15)', color: '#889', flexShrink: 0,
                cursor: 'pointer', fontSize: 11.5, fontWeight: 700, opacity: publishing ? 0.6 : 1
              }}
            >Take it out</button>
          </div>
        ) : (
          <>
            <div style={{ color: '#aab', fontSize: 13.5, lineHeight: 1.6, marginBottom: 10 }}>
              Nobody to send it to? List {pool.name} in Discover Pools and people
              looking for a pool can find it and join without a code.
            </div>
            <button
              onClick={() => onTogglePublic(pool)}
              disabled={publishing}
              style={{
                padding: '11px 18px', borderRadius: 8, cursor: 'pointer',
                background: 'rgba(76,175,80,0.16)', border: '1px solid rgba(76,175,80,0.45)',
                color: '#81c784', fontFamily: "'Oswald', sans-serif", fontSize: 13.5,
                fontWeight: 700, letterSpacing: 0.6, opacity: publishing ? 0.6 : 1
              }}
            >{publishing ? 'LISTING...' : 'LIST IT IN DISCOVER'}</button>
          </>
        )}
      </div>
    </div>
  );
}

function RankBadge({ rank, memberCount }) {
  /*
   * A pool of one has no standings, and saying "1st of 1" in a gold trophy is
   * the single most misleading thing on a new commissioner's screen. It reads as
   * an achievement on a card whose real state is "nobody is here yet", and it
   * sits in the most valuable pixels on that card. The empty-pool prompt below
   * takes this space instead, and asks for the thing that actually needs doing.
   */
  if (!memberCount || memberCount <= 1) return null;

  const isFirst = rank === 1;
  const isSecond = rank === 2;
  const isThird = rank === 3;
  const label = rank === 1 ? '1st' : rank === 2 ? '2nd' : rank === 3 ? '3rd' : rank + 'th';
  const bg = isFirst
    ? 'linear-gradient(135deg,#b8860b,#ffd700,#ffe066,#ffd700)'
    : isSecond
    ? 'linear-gradient(135deg,#5a6472,#c0c0c0)'
    : isThird
    ? 'linear-gradient(135deg,#7a4010,#cd7f32)'
    : 'linear-gradient(135deg,#012244,#013369)';
  const color = (isFirst || isSecond) ? '#111' : '#fff';
  return (
    <div style={{
      background: bg,
      color,
      borderRadius: 10,
      padding: '8px 14px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      minWidth: 64,
      flexShrink: 0,
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: isFirst ? '0 0 16px rgba(255,215,0,0.4)' : isSecond ? '0 0 10px rgba(192,192,192,0.2)' : 'none',
    }}>
      {isFirst && (
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 2s infinite',
        }} />
      )}
      <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.75, letterSpacing: 0.8, position:'relative' }}>RANK</div>
      <div style={{ fontFamily:"'Oswald', sans-serif", fontSize: 22, fontWeight: 700, lineHeight: 1, position:'relative' }}>{label}</div>
      <div style={{ fontSize: 10, opacity: 0.65, position:'relative' }}>of {memberCount}</div>
      <style>{`
        @keyframes shimmer {
          0%   { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </div>
  );
}

/* ── Pick progress bar ────────────────────────────────────────────────── */
function PickProgress({ pool, currentWeek }) {
  if (!currentWeek) return null;
  const { week_pick_count, total_games_this_week, picks_locked } = pool;
  if (total_games_this_week === 0) return null;

  const pct = total_games_this_week > 0 ? (week_pick_count / total_games_this_week) * 100 : 0;
  const done = week_pick_count >= total_games_this_week;
  const barColor = picks_locked ? '#555' : done ? '#4caf50' : week_pick_count > 0 ? '#ffa000' : '#D50A0A';
  const label = picks_locked
    ? 'LOCKED'
    : done
    ? 'ALL PICKS IN'
    : week_pick_count > 0
    ? 'PICKS PENDING'
    : 'NO PICKS YET';

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:5 }}>
        <span style={{ fontSize:11, fontWeight:700, color: barColor, letterSpacing:0.8 }}>{label}</span>
        <span style={{ fontSize:12, fontWeight:700, color:'#aab' }}>
          {week_pick_count}<span style={{ color:'#445', fontWeight:400 }}>/{total_games_this_week}</span>
        </span>
      </div>
      <div style={{ height:6, background:'rgba(255,255,255,0.08)', borderRadius:4, overflow:'hidden' }}>
        <div style={{
          height:'100%',
          width: pct + '%',
          background: barColor,
          borderRadius: 4,
          transition: 'width 0.6s ease',
          boxShadow: done ? '0 0 8px ' + barColor : 'none',
        }} />
      </div>
    </div>
  );
}

/* ── Pool score card ──────────────────────────────────────────────────── */
/**
 * Hand the invite to whatever the device already uses to talk to people.
 *
 * Deliberately not an SMS the server sends. That would mean a Twilio number,
 * per-message cost, US carrier A2P registration, and holding everyone's phone
 * number — to deliver a text from an unknown number telling someone to enter a
 * code, which reads exactly like a scam. This sends the same words from the
 * commissioner's own number, to someone who knows them, for nothing.
 */
async function shareInvite(pool) {
  const url = `${window.location.origin}/?code=${encodeURIComponent(pool.invite_code)}`;
  const text = `Join my Rivalry Picks pool "${pool.name}". Code: ${pool.invite_code}`;
  if (navigator.share) {
    try {
      await navigator.share({ title: 'Rivalry Picks', text, url });
      return;
    } catch (e) { /* dismissed the sheet, or not allowed — fall through */ }
  }
  try {
    await navigator.clipboard.writeText(`${text}\n${url}`);
    alert('Invite copied. Paste it into a text or a group chat.');
  } catch (e) {
    alert(`${text}\n${url}`);
  }
}

function PoolCard({ pool, currentWeek, user, onViewPool, expanded, onToggleExpand, poolDetails, onLoadDetails, renameState, onRenameChange, onRename, onRegenCode, codeState, onCodeChange, onSetCode, onTogglePublic, togglingPublic, publicMsg, onToggleDoubleEntry, togglingDouble, onAddEntry, addingEntry, onRemoveMember, onLeave, onDelete, onStatsClick, copied, onCopy, membership, onPay, paying }) {
  // Season membership state for THIS pool.
  //
  // Paying is available whenever Stripe is configured — not gated on the paywall
  // being on — so players can buy in early instead of hitting a wall the day it
  // flips. Comped users see a "plays free" badge instead of a price. Admins are
  // comped too but still get the button, since paying is the only way to verify
  // the Stripe -> webhook -> database path actually works.
  const mePool = membership && (membership.pools || []).find(p => p.pool_id === pool.id);
  const isAdmin = !!(user && user.is_admin);
  const paidHere = !!(mePool && mePool.paid);

  // A pool with one member, seen by the person who can do something about it.
  const isEmptyPool = !!pool.isCommissioner && pool.member_count <= 1;
  // What this pool would sell them next: nothing, one entry, the $15 two-entry
  // bundle, or a second entry at full price because the bundle window has shut.
  const offer = mePool && mePool.nextPurchase;
  const canPay = !!(membership && membership.checkoutAvailable && offer);
  const showPay = canPay && (!membership.exempt || isAdmin);
  const showFreeBadge = !!(mePool && !paidHere && membership && membership.exempt && !isAdmin);
  const dollars = c => '$' + Math.round((c || 0) / 100);
  const showBundle = !!(showPay && offer.bundleAvailable && membership.bundleAvailable);
  // Is a second seat available to them right now? The server answers this —
  // deriving it here from entriesPaid vs entriesHeld got it wrong whenever
  // payment wasn't required (paywall off, or a comped account), because then
  // nobody has an entriesPaid and the button could never appear.
  const canClaimEntry = !!(mePool && mePool.canAddEntry);
  // Say why the seat is available, accurately. It is NOT always because they
  // paid — a free second entry is exactly what a switched-off paywall or a
  // comped account gets you, and telling someone they paid for something they
  // didn't is the kind of small lie that turns into a support conversation.
  const claimTitle =
    (mePool && mePool.entriesPaid > mePool.entriesHeld)
      ? "You've paid for a second entry in this pool. This creates the sheet."
      : (membership && membership.exempt)
        ? 'You play free, so a second pick sheet costs nothing. This creates it.'
        : 'Memberships are switched off right now, so a second pick sheet is free. This creates it.';

  return (
    <div style={{
      background: '#0d1525',
      border: pool.isCommissioner ? '1px solid rgba(127,179,255,0.2)' : '1px solid rgba(255,255,255,0.07)',
      borderRadius: 14,
      overflow: 'hidden',
      marginBottom: 14,
      boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
      transition: 'box-shadow 0.2s',
    }}>
      {/* ── Card header bar ── */}
      <div style={{
        background: 'linear-gradient(135deg, #012550 0%, #001830 100%)',
        borderBottom: '3px solid #D50A0A',
        padding: '14px 18px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 12,
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display:'flex', gap:6, marginBottom:6, flexWrap:'wrap' }}>
            {pool.isCommissioner && (
              <span style={{ background:'rgba(213,10,10,0.25)', border:'1px solid rgba(213,10,10,0.4)', color:'#ff8888', fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:10, letterSpacing:0.8 }}>
                COMMISSIONER
              </span>
            )}
            {!!pool.is_public && (
              <span style={{ background:'rgba(76,175,80,0.15)', border:'1px solid rgba(76,175,80,0.35)', color:'#81c784', fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:10, letterSpacing:0.8 }}>
                IN DISCOVER
              </span>
            )}
          </div>
          {publicMsg && publicMsg.id === pool.id && (
            <div style={{ fontSize:12.5, color:'#81c784', fontWeight:600, marginBottom:6 }}>
              {publicMsg.text}
            </div>
          )}
          <div style={{ fontFamily:"'Oswald', sans-serif", fontSize:22, fontWeight:700, color:'#fff', lineHeight:1.2, marginBottom:3 }}>
            {pool.name}
          </div>
          <div style={{ fontSize:12, color:'#5a7090' }}>
            {pool.member_count} member{pool.member_count !== 1 ? 's' : ''} &nbsp;&middot;&nbsp; Commish: {pool.commissioner_name}
          </div>
        </div>
        <RankBadge rank={pool.my_rank} memberCount={pool.member_count} />
      </div>

      {/* ── Card body ── */}
      <div style={{ padding:'14px 18px' }}>

        {/* Pick progress */}
        <PickProgress pool={pool} currentWeek={currentWeek} />

        {/* Nobody here but the commissioner. See EmptyPoolPrompt for why this
            outranks the scoreboard. Members of somebody else's one-person pool
            are not shown it: inviting is not their job and the code is not
            theirs to hand out. */}
        {isEmptyPool && (
          <EmptyPoolPrompt
            pool={pool}
            onShare={() => shareInvite(pool)}
            onCopy={() => onCopy(pool.invite_code, pool.id)}
            copied={copied}
            onTogglePublic={onTogglePublic}
            publishing={togglingPublic === pool.id}
          />
        )}

        {/* Stats row */}
        <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap' }}>
          <div style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:8, padding:'6px 12px', display:'flex', alignItems:'center', gap:6 }}>
            <span style={{ fontSize:16, fontWeight:700, color:'#7fb3ff', fontFamily:"'Oswald', sans-serif" }}>{pool.my_correct}</span>
            <span style={{ fontSize:11, color:'#445', fontWeight:700, letterSpacing:0.5 }}>CORRECT</span>
          </div>
          {/* Season membership for this pool */}
          {paidHere && (
            <div style={{ background:'rgba(76,175,80,0.08)', border:'1px solid rgba(76,175,80,0.28)', borderRadius:8, padding:'6px 12px', display:'flex', alignItems:'center', gap:6 }}>
              <span style={{ fontSize:12, color:'#81c784', fontWeight:700, letterSpacing:0.5 }}>
                ✓ MEMBER{mePool.entriesPaid > 1 ? ` · ${mePool.entriesPaid} ENTRIES` : ''}
              </span>
            </div>
          )}
          {showFreeBadge && (
            <div style={{ background:'rgba(127,179,255,0.08)', border:'1px solid rgba(127,179,255,0.25)', borderRadius:8, padding:'6px 12px' }}>
              <span style={{ fontSize:12, color:'#7fb3ff', fontWeight:700, letterSpacing:0.5 }}>PLAYS FREE</span>
            </div>
          )}
          {showPay && (
            <button
              onClick={() => onPay && onPay(pool.id, 1)}
              disabled={paying}
              title={
                offer.addingEntry
                  ? 'A second entry added later is full price. The $15 pair is only offered on a first purchase'
                  : isAdmin && membership.exempt
                    ? 'Admins play free. This is here so you can test the payment flow end to end'
                    : membership.paywallActive
                      ? 'Season membership for this pool'
                      : "Pay early and you'll be set when memberships go live"
              }
              style={{ background: paying ? '#555' : 'linear-gradient(135deg,#D50A0A,#a00808)', border:'none', borderRadius:8, padding:'6px 14px', color:'#fff', fontSize:12, fontWeight:700, cursor: paying ? 'default' : 'pointer', fontFamily:"'Oswald', sans-serif", letterSpacing:0.5 }}
            >{paying ? 'OPENING…'
                : offer.addingEntry ? `${dollars(offer.priceCents)} · 2ND ENTRY`
                : (isAdmin && membership.exempt) ? `PAY ${dollars(offer.priceCents)} · TEST`
                : `PAY ${dollars(offer.priceCents)} · SEASON`}</button>
          )}
          {showBundle && (
            <button
              onClick={() => onPay && onPay(pool.id, 2)}
              disabled={paying}
              title="Two entries in this pool. Only on offer now. Adding a second one later costs another full $10."
              style={{ background:'rgba(127,179,255,0.1)', border:'1px solid rgba(127,179,255,0.4)', borderRadius:8, padding:'6px 14px', color:'#7fb3ff', fontSize:12, fontWeight:700, cursor: paying ? 'default' : 'pointer', fontFamily:"'Oswald', sans-serif", letterSpacing:0.5 }}
            >{dollars(offer.bundle.priceCents)} · 2 ENTRIES</button>
          )}
          {/* Paid for a second entry but haven't taken the seat yet — claiming
              it is a separate step, so a bundle buyer isn't landed with a second
              blank sheet they never asked to fill in. */}
          {canClaimEntry && (
            <button
              onClick={() => onAddEntry(pool.id)}
              disabled={addingEntry === pool.id}
              title={claimTitle}
              style={{ background:'rgba(76,175,80,0.12)', border:'1px solid rgba(76,175,80,0.4)', borderRadius:8, padding:'6px 14px', color:'#81c784', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:"'Oswald', sans-serif", letterSpacing:0.5 }}
            >{addingEntry === pool.id ? 'ADDING…' : '+ CLAIM 2ND ENTRY'}</button>
          )}
        </div>

        {/* Invite code. Hidden while the empty-pool prompt is up, which already
            carries the code and both ways of sending it. Two copies of the same
            control on one card just splits the attention it was meant to get. */}
        {!isEmptyPool && (
        <div style={{ background:'rgba(0,0,0,0.3)', border:'1px dashed rgba(127,179,255,0.2)', borderRadius:8, padding:'8px 14px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, marginBottom:14 }}>
          <div>
            <div style={{ fontSize:9, color:'#5a7090', fontWeight:700, letterSpacing:1.2, marginBottom:2 }}>INVITE CODE</div>
            <div style={{ fontFamily:"'Oswald', sans-serif", fontSize:20, fontWeight:700, color:'#fff', letterSpacing:5 }}>{pool.invite_code}</div>
          </div>
          <div style={{ display:'flex', gap:6, flexShrink:0 }}>
            {/* Hands the invite to the phone's own share sheet — Messages,
                WhatsApp, whatever they use — so it arrives from a number the
                recipient recognises. Falls back to the clipboard on desktop. */}
            <button
              onClick={() => shareInvite(pool)}
              style={{ padding:'6px 14px', borderRadius:6, background:'rgba(127,179,255,0.1)', border:'1px solid rgba(127,179,255,0.25)', color:'#7fb3ff', cursor:'pointer', fontSize:12, fontWeight:700, flexShrink:0 }}
            >
              Share
            </button>
            <button
              onClick={() => onCopy(pool.invite_code, pool.id)}
              style={{ padding:'6px 14px', borderRadius:6, background: copied ? 'rgba(76,175,80,0.2)' : 'rgba(127,179,255,0.1)', border: copied ? '1px solid rgba(76,175,80,0.4)' : '1px solid rgba(127,179,255,0.25)', color: copied ? '#4caf50' : '#7fb3ff', cursor:'pointer', fontSize:12, fontWeight:700, transition:'all 0.2s', flexShrink:0 }}
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>
        )}

        {/* Expanded members + controls */}
        {expanded && (
          <div style={{ borderTop:'1px solid rgba(255,255,255,0.06)', paddingTop:14, marginBottom:14 }}>
            {poolDetails ? (
              <div>
                <div style={{ fontSize:10, color:'#445', fontWeight:700, letterSpacing:1, marginBottom:8 }}>MEMBERS</div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:12 }}>
                  {poolDetails.members.map(function(m) {
                    var canRemove = (pool.isCommissioner || (user && user.is_admin)) && !m.is_commissioner;
                    return (
                      <div
                        key={m.id}
                        onClick={() => onStatsClick(m.id)}
                        onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.3)'; }}
                        onMouseLeave={e => { e.currentTarget.style.filter = 'brightness(1)'; }}
                        style={{ background: m.is_commissioner ? 'rgba(213,10,10,0.12)' : 'rgba(255,255,255,0.06)', border: m.is_commissioner ? '1px solid rgba(213,10,10,0.25)' : 'none', borderRadius:20, padding:'4px 10px', fontSize:12, color: m.is_commissioner ? '#ff9999' : '#aab', display:'flex', alignItems:'center', gap:6, cursor:'pointer' }}
                      >
                        {m.is_commissioner ? '* ' : ''}{m.username}
                        {canRemove && (
                          <button
                            onClick={e => { e.stopPropagation(); onRemoveMember(pool.id, m.id, m.username); }}
                            style={{ background:'none', border:'none', cursor:'pointer', color:'#f44336', fontSize:14, padding:0, lineHeight:1 }}
                          >x</button>
                        )}
                      </div>
                    );
                  })}
                </div>
                {(pool.isCommissioner || (user && user.is_admin)) && (
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
                    <input
                      placeholder="Rename pool..."
                      value={renameState || ''}
                      onChange={e => onRenameChange(pool.id, e.target.value)}
                      maxLength={50}
                      style={{ padding:'6px 10px', background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.15)', borderRadius:6, color:'#fff', fontSize:13, outline:'none', width:170 }}
                    />
                    <button onClick={() => onRename(pool.id)} disabled={!renameState || !renameState.trim()} style={{ padding:'6px 12px', borderRadius:6, background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', color:'#778', cursor:'pointer', fontSize:12, fontWeight:700 }}>Rename</button>
                    <button onClick={() => onRegenCode(pool.id)} style={{ padding:'6px 12px', borderRadius:6, background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', color:'#778', cursor:'pointer', fontSize:11, fontWeight:700 }}>New Code</button>
                    {/* A code people can be told out loud beats reading X7K2QM
                        down a phone. Letters and numbers only — an invite email
                        links to /?code=..., and anything else would make a link
                        that opens the app and drops the code on the floor. */}
                    <input
                      placeholder="Custom code..."
                      value={codeState || ''}
                      onChange={e => onCodeChange(pool.id, e.target.value)}
                      maxLength={14}
                      style={{ padding:'6px 10px', background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.15)', borderRadius:6, color:'#fff', fontSize:13, outline:'none', width:130, textTransform:'uppercase' }}
                    />
                    <button
                      onClick={() => onSetCode(pool.id)}
                      disabled={!codeState || codeState.replace(/[\s-]+/g,'').length < 4}
                      style={{ padding:'6px 12px', borderRadius:6, background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', color:'#778', cursor:'pointer', fontSize:11, fontWeight:700 }}
                    >Set Code</button>
                    {/* Off by default, like survivor and confirmations — a second
                        entry changes the board for everyone in the pool. */}
                    <button
                      onClick={() => onToggleDoubleEntry(pool.id, !pool.double_entry_enabled)}
                      disabled={togglingDouble === pool.id}
                      title={pool.double_entry_enabled
                        ? 'Members can buy a second entry. Switching this off stops new ones; entries already paid for stay.'
                        : 'Let members buy a second, independent pick sheet in this pool (max two each).'}
                      style={{ padding:'6px 12px', borderRadius:6, background: pool.double_entry_enabled ? 'rgba(127,179,255,0.15)' : 'rgba(255,255,255,0.06)', border: pool.double_entry_enabled ? '1px solid rgba(127,179,255,0.4)' : '1px solid rgba(255,255,255,0.1)', color: pool.double_entry_enabled ? '#7fb3ff' : '#778', cursor:'pointer', fontSize:11, fontWeight:700, opacity: togglingDouble === pool.id ? 0.6 : 1 }}
                    >
                      {pool.double_entry_enabled ? '2️⃣ Double entry ON' : '2️⃣ Allow 2nd entry'}
                    </button>
                    {/* Was admin-only, which is why no pool could ever fill
                        itself. The commissioner's own call now, same as the two
                        switches beside it. */}
                    <button
                      onClick={() => onTogglePublic(pool)}
                      disabled={togglingPublic === pool.id}
                      title={pool.is_public
                        ? 'Listed in Discover Pools. Taking it out stops anyone new finding it; everyone already in stays in.'
                        : 'List this pool in Discover so people with no invite code can find it and join.'}
                      style={{ padding:'6px 12px', borderRadius:6, background: pool.is_public ? 'rgba(76,175,80,0.2)' : 'rgba(255,255,255,0.06)', border: pool.is_public ? '1px solid rgba(76,175,80,0.4)' : '1px solid rgba(255,255,255,0.1)', color: pool.is_public ? '#81c784' : '#778', cursor:'pointer', fontSize:11, fontWeight:700, opacity: togglingPublic === pool.id ? 0.6 : 1 }}
                    >
                      {pool.is_public ? '🌐 In Discover (take out)' : '🌐 List in Discover'}
                    </button>
                  </div>
                )}
                {/* Skipped while the empty-pool prompt is showing, which already
                    has this same form above and closer to the ask. */}
                {!isEmptyPool && (pool.isCommissioner || (user && user.is_admin)) && <PoolInvite pool={pool} />}
                <ConfirmationsPanel poolId={pool.id} user={user} />
                <div style={{ marginTop:16 }}>
                  <PoolChat poolId={pool.id} user={user} />
                </div>
              </div>
            ) : (
              <div style={{ color:'#445', fontSize:13 }}>Loading...</div>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          <button onClick={() => onViewPool(pool)} style={{ padding:'9px 16px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, fontWeight:700, background:'linear-gradient(135deg,#D50A0A,#a00808)', color:'#fff', fontFamily:"'Oswald', sans-serif", letterSpacing:0.5 }}>
            STANDINGS
          </button>
          <button onClick={() => { onToggleExpand(pool.id); if (!expanded) onLoadDetails(pool.id); }} style={{ padding:'9px 14px', borderRadius:8, border:'1px solid rgba(255,255,255,0.1)', cursor:'pointer', fontSize:12, fontWeight:700, background:'rgba(255,255,255,0.05)', color:'#778' }}>
            {expanded ? 'Hide' : 'Members (' + pool.member_count + ')'}
          </button>
          {(pool.isCommissioner || (user && user.is_admin)) && (
            <button onClick={() => onDelete(pool.id, pool.name)} style={{ padding:'9px 14px', borderRadius:8, border:'1px solid rgba(213,10,10,0.2)', cursor:'pointer', fontSize:12, fontWeight:700, background:'rgba(213,10,10,0.08)', color:'#ff6666', marginLeft:'auto' }}>
              Delete
            </button>
          )}
          {!pool.isCommissioner && (
            <button onClick={() => onLeave(pool.id, pool.name)} style={{ padding:'9px 14px', borderRadius:8, border:'1px solid rgba(213,10,10,0.2)', cursor:'pointer', fontSize:12, fontWeight:700, background:'rgba(213,10,10,0.08)', color:'#ff6666', marginLeft:'auto' }}>
              Leave
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */
export default function PoolsPage({ user, onViewPool, membership, onMembershipChange, inviteCode, onInviteHandled }) {
  const [pools, setPools] = useState([]);
  const [publicPools, setPublicPools] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterMode, setFilterMode] = useState('all');
  const [paying, setPaying] = useState(null);

  const [newName, setNewName] = useState('');
  const [createMsg, setCreateMsg] = useState(null);
  const [creating, setCreating] = useState(false);
  const [joinCode, setJoinCode] = useState(inviteCode || '');
  const [joinMsg, setJoinMsg] = useState(null);
  const [joining, setJoining] = useState(false);

  const [expandedPool, setExpandedPool] = useState(null);
  const [poolDetails, setPoolDetails] = useState({});
  const [renameState, setRenameState] = useState({});
  const [copied, setCopied] = useState(null);
  const [statsUserId, setStatsUserId] = useState(null);
  const [togglingPublic, setTogglingPublic] = useState(null);
  // What just happened to a pool's listing. Said on the card rather than in an
  // alert, because it is confirmation of something that worked, not a question.
  const [publicMsg, setPublicMsg] = useState(null);
  const [togglingDouble, setTogglingDouble] = useState(null);
  const [addingEntry, setAddingEntry] = useState(null);
  const [joiningPublic, setJoiningPublic] = useState(null);
  const [publicJoinMsg, setPublicJoinMsg] = useState({});
  const [confetti, setConfetti] = useState(false);

  const loadPools = useCallback(async () => {
    try {
      const [mineRes, pubRes] = await Promise.all([
        API.get('/pools/mine'),
        API.get('/pools/public').catch(() => ({ data: { pools: [] } }))
      ]);
      setPools(mineRes.data.pools || []);
      setCurrentWeek(mineRes.data.current_week || null);
      setPublicPools(pubRes.data.pools || []);
      // Membership lives in App and drove the paywall's per-pool "Pay $10 ·
      // <pool>" buttons, but nothing refetched it after joining, creating, or
      // leaving a pool — so a pool you just joined had no pay button until a
      // full page reload. Every one of those handlers already calls loadPools,
      // so refreshing here covers all of them.
      if (onMembershipChange) onMembershipChange();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [onMembershipChange]);

  useEffect(() => { loadPools(); }, [loadPools]);

  const loadPoolDetails = async (poolId) => {
    try {
      const res = await API.get('/pools/' + poolId);
      setPoolDetails(prev => ({ ...prev, [poolId]: res.data }));
    } catch (e) {}
  };

  const triggerConfetti = () => {
    setConfetti(true);
    setTimeout(() => setConfetti(false), 1500);
  };

  const createPool = async () => {
    if (!newName.trim()) return;
    setCreating(true); setCreateMsg(null);
    try {
      const res = await API.post('/pools', { name: newName.trim() });
      setCreateMsg({ ok: true, text: 'Pool created! Code: ' + res.data.pool.invite_code });
      setNewName('');
      triggerConfetti();
      await loadPools();
    } catch (err) {
      setCreateMsg({ ok: false, text: err.response?.data?.error || 'Failed' });
    } finally { setCreating(false); }
  };

  useEffect(() => {
    if (inviteCode) setJoinCode(inviteCode);
  }, [inviteCode]);

  const joinPool = async () => {
    if (!joinCode.trim()) return;
    setJoining(true); setJoinMsg(null);
    try {
      const res = await API.post('/pools/join', { invite_code: joinCode.trim().toUpperCase() });
      setJoinMsg({ ok: true, text: res.data.message });
      setJoinCode('');
      if (onInviteHandled) onInviteHandled();
      triggerConfetti();
      await loadPools();
    } catch (err) {
      setJoinMsg({ ok: false, text: err.response?.data?.error || 'Invalid code' });
      // Already a member, bad code, whatever — the invite has had its moment.
      if (onInviteHandled) onInviteHandled();
    } finally { setJoining(false); }
  };

  const joinPublicPool = async (poolId, poolName) => {
    setJoiningPublic(poolId);
    try {
      await API.post('/pools/' + poolId + '/join-public');
      setPublicJoinMsg(prev => ({ ...prev, [poolId]: { ok: true, text: 'Joined "' + poolName + '"!' } }));
      triggerConfetti();
      await loadPools();
    } catch (err) {
      setPublicJoinMsg(prev => ({ ...prev, [poolId]: { ok: false, text: err.response?.data?.error || 'Failed' } }));
    } finally { setJoiningPublic(null); }
  };

  const copyCode = (code, poolId) => {
    navigator.clipboard.writeText(code).catch(() => {});
    setCopied(poolId);
    setTimeout(() => setCopied(null), 2000);
  };

  // Season membership for one pool — hosted Stripe Checkout, so just redirect.
  // `entries` is 1 or 2; 2 buys the $15 pair, which Stripe only offers on a
  // first purchase (the backend decides, not this call).
  const startCheckout = async (poolId, entries = 1) => {
    setPaying(poolId);
    try {
      const res = await API.post('/membership/checkout', { pool_id: poolId, entries });
      if (res.data?.url) window.location.href = res.data.url;
      else { alert('Could not start checkout.'); setPaying(null); }
    } catch (err) {
      alert(err.response?.data?.error || 'Could not start checkout.');
      setPaying(null);
    }
  };

  const leavePool = async (poolId, name) => {
    if (!window.confirm('Leave "' + name + '"?')) return;
    try { await API.post('/pools/' + poolId + '/leave'); await loadPools(); }
    catch (err) { alert(err.response?.data?.error || 'Failed'); }
  };

  const [codeState, setCodeState] = useState({});
  const setCustomCode = async (poolId) => {
    const wanted = codeState[poolId];
    if (!wanted?.trim()) return;
    try {
      const res = await API.put('/pools/' + poolId + '/invite-code', { invite_code: wanted });
      setCodeState(prev => ({ ...prev, [poolId]: '' }));
      await loadPools();
      // Say what it became when it isn't what they typed — nobody should print
      // "Free Play" on something when the code is FREEPLAY.
      alert(res.data.normalizedFrom
        ? `Invite code set to ${res.data.invite_code}. Spaces aren't allowed in the link, so "${res.data.normalizedFrom}" became ${res.data.invite_code}.`
        : `Invite code set to ${res.data.invite_code}`);
    } catch (err) {
      alert(err.response?.data?.error || 'Could not set that code');
    }
  };

  const deletePool = async (poolId, name) => {
    if (!window.confirm('Delete "' + name + '"? This cannot be undone.')) return;
    try { await API.delete('/pools/' + poolId); await loadPools(); }
    catch (err) { alert(err.response?.data?.error || 'Failed'); }
  };

  const removeMember = async (poolId, memberId, memberName) => {
    if (!window.confirm('Remove ' + memberName + '?')) return;
    try {
      await API.delete('/pools/' + poolId + '/members/' + memberId);
      await loadPoolDetails(poolId);
      await loadPools();
    } catch (err) { alert(err.response?.data?.error || 'Failed'); }
  };

  const renamePool = async (poolId) => {
    const name = renameState[poolId];
    if (!name?.trim()) return;
    try {
      await API.put('/pools/' + poolId, { name: name.trim() });
      setRenameState(prev => ({ ...prev, [poolId]: '' }));
      await loadPools();
    } catch (err) { alert(err.response?.data?.error || 'Failed'); }
  };

  const regenerateCode = async (poolId) => {
    if (!window.confirm('Generate a new invite code? The old one will stop working.')) return;
    try {
      const res = await API.post('/pools/' + poolId + '/regenerate-code');
      await loadPools(); await loadPoolDetails(poolId);
      alert('New code: ' + res.data.invite_code);
    } catch (err) { alert('Failed'); }
  };

  /**
   * Publishing is asked about. Unpublishing just happens.
   *
   * Turning it on exposes the people already in the pool to anyone who joins,
   * which is not something a globe icon conveys, so it is confirmed and the
   * warning counts them. Turning it off takes nothing away from anybody already
   * in, so making that one a two-step would be friction for no reason.
   */
  const togglePublic = async (pool) => {
    if (!pool.is_public && !window.confirm(publishWarning(pool))) return;
    setTogglingPublic(pool.id);
    try {
      const res = await API.post('/pools/' + pool.id + '/toggle-public');
      await loadPools();
      if (res.data && res.data.message) {
        setPublicMsg({ id: pool.id, text: res.data.message });
        setTimeout(() => setPublicMsg(null), 7000);
      }
    }
    catch (err) { alert(err.response?.data?.error || 'Failed'); }
    finally { setTogglingPublic(null); }
  };

  // Commissioner switch for a second entry per member. Switching it off never
  // deletes an entry somebody already paid for — the server refuses to, and the
  // message it returns says so.
  const toggleDoubleEntry = async (poolId, enabled) => {
    setTogglingDouble(poolId);
    try {
      const res = await API.put('/pools/' + poolId + '/double-entry', { enabled });
      await loadPools();
      if (onMembershipChange) await onMembershipChange();
      if (res.data?.message) alert(res.data.message);
    } catch (err) { alert(err.response?.data?.error || 'Failed'); }
    finally { setTogglingDouble(null); }
  };

  // Take the second seat they've already paid for. Separate from the payment so
  // buying the $15 pair doesn't silently saddle them with a blank second sheet.
  const addEntry = async (poolId) => {
    setAddingEntry(poolId);
    try {
      const res = await API.post('/entries', { pool_id: poolId });
      if (onMembershipChange) await onMembershipChange();
      alert(res.data?.message || 'Second entry added.');
    } catch (err) { alert(err.response?.data?.error || 'Could not add a second entry.'); }
    finally { setAddingEntry(null); }
  };

  const filteredPools = pools.filter(p => {
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filterMode === 'all' || (filterMode === 'mine' && p.isCommissioner);
    return matchSearch && matchFilter;
  });

  if (loading) return (
    <div style={{ padding:'80px 20px', textAlign:'center' }}>
      <div style={{ fontSize:48, marginBottom:16 }}>🏆</div>
      <div style={{ color:'#445', fontFamily:"'Oswald', sans-serif", fontSize:18, letterSpacing:1 }}>LOADING POOLS...</div>
    </div>
  );

  return (
    <div style={{ padding:'24px 20px', maxWidth:860, margin:'0 auto' }}>
      <ConfettiBurst active={confetti} />

      {/* Page header */}
      <div style={{ marginBottom:24 }}>
        <div style={{ fontFamily:"'Oswald', sans-serif", fontSize:30, fontWeight:700, color:'#fff', marginBottom:2, display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ background:'linear-gradient(135deg,#D50A0A,#8b0000)', padding:'2px 10px', borderRadius:6, fontSize:13, fontWeight:700, letterSpacing:1.5, verticalAlign:'middle' }}>NFL</span>
          MY POOLS
        </div>
        <div style={{ color:'#445', fontSize:14 }}>Your leagues, members, and standings, all in one place.</div>
      </div>

      {/* Create / Join / Redeem panels */}
      {/*
        Two panels, the same two for everybody. This grid used to swap the left
        one between "create a pool" and "enter a creator code from the admin"
        depending on a flag nobody could see, so two people opened this page and
        found different apps — and the version most new arrivals got sent them
        looking for an admin they had no way to reach.
      */}
      <div style={{ display:'grid', gridTemplateColumns: window.innerWidth < 600 ? '1fr' : '1fr 1fr', gap:14, marginBottom:28 }}>
          <div style={{ background:'#0d1525', border:'1px solid rgba(213,10,10,0.2)', borderRadius:12, padding:'18px 20px' }}>
            <div style={{ fontFamily:"'Oswald', sans-serif", fontSize:14, fontWeight:700, color:'#D50A0A', marginBottom:4, letterSpacing:1 }}>
              + CREATE A POOL
            </div>
            <div style={{ color:'#445', fontSize:13, marginBottom:14 }}>Name your pool and share the invite code with your league.</div>
            <input
              style={{ width:'100%', padding:'10px 14px', background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:7, color:'#fff', fontSize:15, outline:'none', marginBottom:12, boxSizing:'border-box' }}
              placeholder="Pool name (e.g. Office Picks 2026)"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && createPool()}
              maxLength={50}
            />
            <button
              onClick={createPool}
              disabled={creating || !newName.trim()}
              style={{ width:'100%', padding:'11px', borderRadius:7, border:'none', cursor:'pointer', fontSize:14, fontWeight:700, fontFamily:"'Oswald', sans-serif", letterSpacing:1, background: creating ? '#555' : 'linear-gradient(135deg,#D50A0A,#a00808)', color:'#fff' }}
            >{creating ? 'Creating...' : 'CREATE POOL'}</button>
            {createMsg && <div style={{ marginTop:10, fontSize:13, fontWeight:600, padding:'8px 12px', borderRadius:6, background: createMsg.ok ? 'rgba(76,175,80,0.12)' : 'rgba(213,10,10,0.12)', color: createMsg.ok ? '#4caf50' : '#ff8888' }}>{createMsg.text}</div>}
          </div>
        <div style={{ background:'#0d1525', border:'1px solid rgba(127,179,255,0.15)', borderRadius:12, padding:'18px 20px' }}>
          <div style={{ fontFamily:"'Oswald', sans-serif", fontSize:14, fontWeight:700, color:'#7fb3ff', marginBottom:4, letterSpacing:1 }}>
            {inviteCode ? "YOU'VE BEEN INVITED" : 'JOIN A POOL'}
          </div>
          <div style={{ color: inviteCode ? '#81c784' : '#445', fontSize:13, marginBottom:14 }}>
            {inviteCode
              ? 'Your invite code is filled in below. Hit Join Pool to accept.'
              : 'Got an invite code? Enter it below to join.'}
          </div>
          {/*
            Sized for the code, not for the old assumption about it.
            Generated codes are 6, but a chosen one can be 4 to 12 — and this
            field capped at 6, required 6 before the button lit up, and showed a
            six-X placeholder. FREEPLAY simply could not be typed here.
            Letter spacing and size step down for a long code so twelve
            characters still fit on a phone.
          */}
          <input
            style={{ width:'100%', padding:'10px 14px', background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:7, color:'#fff', fontSize: joinCode.length > 8 ? 18 : 22, outline:'none', marginBottom:12, boxSizing:'border-box', textTransform:'uppercase', letterSpacing: joinCode.length > 8 ? 2 : 4, fontFamily:"'Oswald', sans-serif", textAlign:'center' }}
            placeholder="INVITE CODE"
            value={joinCode}
            onChange={e => setJoinCode(e.target.value.replace(/[\s-]+/g, '').toUpperCase())}
            onKeyDown={e => e.key === 'Enter' && joinPool()}
            maxLength={12}
          />
          <button
            onClick={joinPool}
            disabled={joining || joinCode.length < 4}
            style={{ width:'100%', padding:'11px', borderRadius:7, border:'none', cursor:'pointer', fontSize:14, fontWeight:700, fontFamily:"'Oswald', sans-serif", letterSpacing:1, background: joining ? '#555' : 'linear-gradient(135deg,#013369,#0a5299)', color:'#fff', boxShadow: inviteCode && !joining ? '0 0 0 2px rgba(129,199,132,0.55)' : 'none' }}
          >{joining ? 'Joining...' : 'JOIN POOL'}</button>
          {joinMsg && <div style={{ marginTop:10, fontSize:13, fontWeight:600, padding:'8px 12px', borderRadius:6, background: joinMsg.ok ? 'rgba(76,175,80,0.12)' : 'rgba(213,10,10,0.12)', color: joinMsg.ok ? '#4caf50' : '#ff8888' }}>{joinMsg.text}</div>}
        </div>
      </div>

      {/* Search + filter */}
      {pools.length > 0 && (
        <div style={{ display:'flex', gap:8, marginBottom:16, alignItems:'center', flexWrap:'wrap' }}>
          <input
            style={{ flex:1, minWidth:160, padding:'9px 14px', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, color:'#fff', fontSize:14, outline:'none' }}
            placeholder="Search pools..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {['all','mine'].map(mode => (
            <button
              key={mode}
              onClick={() => setFilterMode(mode)}
              style={{ padding:'8px 14px', borderRadius:8, border: filterMode === mode ? '1px solid rgba(127,179,255,0.4)' : '1px solid rgba(255,255,255,0.08)', background: filterMode === mode ? 'rgba(1,51,105,0.5)' : 'rgba(255,255,255,0.03)', color: filterMode === mode ? '#7fb3ff' : '#556', cursor:'pointer', fontSize:11, fontWeight:700, letterSpacing:0.8 }}
            >{mode === 'all' ? 'ALL POOLS' : 'MY COMMISH'}</button>
          ))}
        </div>
      )}

      {/* Pool count */}
      {filteredPools.length > 0 && (
        <div style={{ fontSize:11, color:'#334', fontWeight:700, letterSpacing:1, marginBottom:12 }}>
          {filteredPools.length} POOL{filteredPools.length !== 1 ? 'S' : ''}
        </div>
      )}

      {/* Pool cards */}
      {pools.length === 0 ? (
        <div style={{ background:'#0d1525', border:'1px solid rgba(255,255,255,0.06)', borderRadius:14, padding:'40px 24px', textAlign:'center' }}>
          <div style={{ fontSize:48, marginBottom:12 }}>🏈</div>
          <div style={{ fontFamily:"'Oswald', sans-serif", fontSize:22, color:'#556', marginBottom:8 }}>NO POOLS YET</div>
          <div style={{ color:'#334', fontSize:14 }}>
            {/* Branching on is_admin here was the same dead end as the creator-code
                panel: it told everyone else to go find an admin, which for anyone
                arriving on their own means nobody. Both things it points at are
                directly above, and both are open to whoever is reading it. */}
            Start one above and share the code, or enter a code someone sent you.
          </div>
        </div>
      ) : filteredPools.length === 0 ? (
        <div style={{ textAlign:'center', color:'#445', padding:'24px 0', fontSize:14 }}>No pools match your search.</div>
      ) : (
        filteredPools.map(function(pool) {
          return (
            <PoolCard
              key={pool.id}
              pool={pool}
              currentWeek={currentWeek}
              user={user}
              onViewPool={onViewPool}
              expanded={expandedPool === pool.id}
              onToggleExpand={id => setExpandedPool(expandedPool === id ? null : id)}
              poolDetails={poolDetails[pool.id]}
              onLoadDetails={loadPoolDetails}
              renameState={renameState[pool.id] || ''}
              onRenameChange={(id, val) => setRenameState(prev => ({ ...prev, [id]: val }))}
              onRename={renamePool}
              onRegenCode={regenerateCode}
              codeState={codeState[pool.id]}
              onCodeChange={(id, val) => setCodeState(prev => ({ ...prev, [id]: val }))}
              onSetCode={setCustomCode}
              onTogglePublic={togglePublic}
              onToggleDoubleEntry={toggleDoubleEntry}
              togglingDouble={togglingDouble}
              onAddEntry={addEntry}
              addingEntry={addingEntry}
              togglingPublic={togglingPublic}
              publicMsg={publicMsg}
              onRemoveMember={removeMember}
              onLeave={leavePool}
              onDelete={deletePool}
              onStatsClick={setStatsUserId}
              copied={copied === pool.id ? pool.id : null}
              onCopy={copyCode}
              membership={membership}
              onPay={startCheckout}
              paying={paying === pool.id}
            />
          );
        })
      )}

      {/* Discover public pools */}
      <div style={{ borderTop:'1px solid rgba(255,255,255,0.05)', marginTop:28, paddingTop:24 }}>
        <div style={{ fontFamily:"'Oswald', sans-serif", fontSize:18, fontWeight:700, color:'#fff', marginBottom:4, letterSpacing:0.5 }}>
          DISCOVER POOLS
        </div>
        <div style={{ color:'#445', fontSize:13, marginBottom:16 }}>Open pools. Join without an invite code.</div>
        {publicPools.length === 0 ? (
          <div style={ps.emptyText}>No public pools right now.</div>
        ) : (
          publicPools.map(function(pool) {
            var msg = publicJoinMsg[pool.id];
            return (
              <div key={pool.id} style={ps.publicCard}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={ps.publicCardName}>{pool.name}</div>
                  <div style={ps.publicCardMeta}>
                    {pool.member_count} member{pool.member_count !== 1 ? 's' : ''} &nbsp;&middot;&nbsp; {pool.commissioner_name}
                  </div>
                  {msg && (
                    <div style={{ fontSize:12, marginTop:4, color: msg.ok ? '#4caf50' : '#ff8888', fontWeight:700 }}>{msg.text}</div>
                  )}
                </div>
                {(!msg || !msg.ok) && (
                  <button
                    onClick={() => joinPublicPool(pool.id, pool.name)}
                    disabled={joiningPublic === pool.id}
                    style={{ padding:'9px 18px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, fontWeight:700, fontFamily:"'Oswald', sans-serif", letterSpacing:0.5, background:'linear-gradient(135deg,#013369,#0a5299)', color:'#fff', flexShrink:0, opacity: joiningPublic === pool.id ? 0.7 : 1 }}
                  >{joiningPublic === pool.id ? 'Joining...' : '+ JOIN'}</button>
                )}
              </div>
            );
          })
        )}
      </div>

      <div style={{ height:48 }} />
      {statsUserId && (
        <UserStatsModal userId={statsUserId} me={user} onClose={() => setStatsUserId(null)} />
      )}
    </div>
  );
}
